'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getAdventureById } from '@/lib/data'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import styles from '@/styles/adventure-page.module.css'
import { BreadcrumbSchema, AdventureSchema } from '@/lib/schema'
import { useSwiperCleanup } from '@/hooks/useSwiperCleanup'
import GoogleMapEmbed, { GOOGLE_MAPS_EMBEDS } from '@/components/GoogleMapEmbed'


export default function WaterSkiingPage() {
  const swiperRef = useSwiperCleanup()
  const thisAdventure = getAdventureById('water-skiing')
  const t = useTranslations('guidedAdventures')
  const tAdv = useTranslations('adventures')
  const tCommon = useTranslations('common')
  const tLodges = useTranslations('lodges')
  const params = useParams()
  const locale = params.locale as string

  if (!thisAdventure) {
    return <div>{t('common.adventureNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/guided-adventures/` },
        { name: tAdv('water-skiing.title'), href: `/${locale}/guided-adventures/water-skiing/` },
      ]} />
      <AdventureSchema
        name={t('waterSkiing.schema.name')}
        description={t('waterSkiing.schema.description')}
        image={thisAdventure.heroImage}
        url={`/${locale}/guided-adventures/water-skiing/`}
        activityType={t('waterSkiing.schema.activityType')}
        location={t('waterSkiing.schema.location')}
        geo={{ latitude: -43.23, longitude: -72.35 }}
      />
      <Header />

      <HeroParallax
        backgroundImage={thisAdventure.heroImage}
        title={tAdv('water-skiing.title')}
        subtitle={tAdv('water-skiing.subtitle')}
        location={tAdv('water-skiing.location')}
      />

      <main className={styles.pageWrapper}>
        <section className={styles.adventureDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.itineraryColumn}>
                <h3>{t('common.itinerary')}</h3>
                <p className={styles.duration}>{t('waterSkiing.duration')}</p>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 1:</strong> {t('waterSkiing.day1')}
                </div>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 2-3:</strong> {t('waterSkiing.day2to3')}
                </div>

                <div className={styles.itineraryItem}>
                  <strong>{t('common.day')} 4:</strong> {t('waterSkiing.day4')}
                </div>
              </div>

              <div className={styles.mapColumn}>
                <div className={styles.mapWrapper}>
                  <GoogleMapEmbed
                    {...GOOGLE_MAPS_EMBEDS['villarrica']}
                    
                  />
                </div>
              </div>

              <div className={styles.includedColumn}>
                <h3>{t('common.whatsIncluded')}</h3>
                <ul>
                  <li>{t('waterSkiing.included.item1')}</li>
                  <li>{t('waterSkiing.included.item2')}</li>
                  <li>{t('waterSkiing.included.item3')}</li>
                  <li>{t('waterSkiing.included.item4')}</li>
                  <li>{t('waterSkiing.included.item5')}</li>
                  <li>{t('waterSkiing.included.item6')}</li>
                  <li>{t('waterSkiing.included.item7')}</li>
                  <li>{t('waterSkiing.included.item8')}</li>
                  <li>{t('waterSkiing.included.item9')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.adventures}>
          <div className="container">
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t('common.personalizedAdventures')}</h2>
              <p className={styles.sectionDescription}>
                {t('common.personalizedDescription')}
              </p>
            </div>

            <div className={styles.adventuresCarousel}>
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                spaceBetween={24}
                loop={true}
                grabCursor={true}
                navigation={{
                  nextEl: `.${styles.swiperButtonNext}`,
                  prevEl: `.${styles.swiperButtonPrev}`,
                }}
                breakpoints={{
                  640: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 3, spaceBetween: 32 },
                }}
                onSwiper={(swiper) => { swiperRef.current = swiper }}
                className={styles.adventuresSwiper}
              >
                {[
                  { id: 1, src: '/images/adventures/water-skiing/1.webp', alt: t('waterSkiing.alts.gallery1') },
                  { id: 2, src: '/images/adventures/water-skiing/2.webp', alt: t('waterSkiing.alts.gallery2') },
                  { id: 3, src: '/images/adventures/water-skiing/3.webp', alt: t('waterSkiing.alts.gallery3') },
                  { id: 4, src: '/images/adventures/water-skiing/6.webp', alt: t('waterSkiing.alts.gallery4') },
                  { id: 5, src: '/images/adventures/water-skiing/7.webp', alt: t('waterSkiing.alts.gallery5') },
                ].map((image) => (
                  <SwiperSlide key={`water-skiing-${image.id}`}>
                    <article className={styles.adventureCard}>
                      <div className={styles.adventureImage}>
                        <img src={image.src} alt={image.alt} loading="lazy" />
                      </div>
                    </article>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div className={`${styles.swiperButtonPrev} swiper-button-prev`}></div>
              <div className={`${styles.swiperButtonNext} swiper-button-next`}></div>
            </div>

            <div className={styles.sectionCta}>
              <a href={`/${locale}/guided-adventures/`} className="btn-primary">
                {t('common.exploreAdventures')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <section className={styles.creamSection}>
          <div className="container">
            <h2>{t('common.whereToStay')}</h2>
            <div className={styles.lodgesGrid}>
              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-yelcho-patagonia/yelcho-en-la-patagonia_exterior_hotel_1-1-qtpgs4svvtox39co7ll9fi4dr08v34gfk5agnwmmq0.avif"
                    alt={`${tLodges('apex-yelcho-patagonia.name')} - Remote wilderness lodge on pristine Lago Yelcho, Patagonia`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-yelcho-patagonia.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-yelcho-patagonia.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-yelcho-patagonia/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>

              <article className={styles.lodgeCard}>
                <div className={styles.lodgeImage}>
                  <img
                    src="/images/lodges/apex-patagonia-lodge/Copia-de-6Las-Gualas--768x576.avif"
                    alt={`${tLodges('apex-patagonia-lodge.name')} - Premier fly fishing lodge with 12km private river access`}
                    loading="lazy"
                  />
                </div>
                <div className={styles.lodgeOverlay}>
                  <h3 className={styles.lodgeTitle}>{tLodges('apex-patagonia-lodge.name')}</h3>
                  <div className={styles.lodgeContent}>
                    <p>{tLodges('apex-patagonia-lodge.subtitle')}</p>
                    <a href={`/${locale}/lodges/apex-patagonia-lodge/`} className={styles.lodgeLink}>
                      {t('common.explore')} &rarr;
                    </a>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  )
}
